import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './style.less';
import { useDisclosure, useWindowSize } from '@/hooks';
import { useNotification } from '@/contexts/WebsocketProvider/Notification.provider';
import ActionIcon from '../_common/ActionIcon';
import { X } from 'react-feather';
import { LoadingOutlined } from '@ant-design/icons';
import {
  UserNotification,
  UserNotificationOriginEnum,
} from '@/core/domain/communication/userNotification/userNotification.types';
import {
  useUserNotificationListFilter,
  useUserNotificationMarkAsDismissed,
  useUserNotificationMarkAsRead,
} from '@/core/domain/communication/userNotification/userNotification.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { Checkbox, message, Spin } from 'antd';
import { useIntl } from 'react-intl';

export default function SystemAlertBar() {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const intl = useIntl();

  const [dontShowMarqueeAgain, setDontShowMarqueeAgain] = useState(false);

  const { width, isMobile } = useWindowSize();

  const notification = useNotification();
  const userNotificationMarkRead = useUserNotificationMarkAsRead();
  const userNotificationMarkAsDismissed = useUserNotificationMarkAsDismissed();
  const userNotificationList = useUserNotificationListFilter({
    isPriority: true,
    isRead: false,
    rows: 1,
  });

  const notifications = flatInfiniteData(userNotificationList.data);

  const userFilteredNotifications = useMemo(
    () =>
      notifications.filter(
        (notification) =>
          notification.expiresAt &&
          new Date(notification.expiresAt) > new Date(),
      ),
    [userNotificationList.data],
  );

  const [userNotification, setUserNotification] = useState<UserNotification>();

  const [visible, action] = useDisclosure(false, {
    onClose: () => {
      setUserNotification(undefined);
      markRead();
    },
  });

  useEffect(() => {
    if (
      ((notification.userNotification &&
        notification.userNotification.isPriority) ||
        userFilteredNotifications.length) &&
      (!userNotificationList.isFetching || !userNotificationMarkRead.isLoading)
    ) {
      setUserNotification(
        notification.userNotification || userFilteredNotifications[0],
      );
    } else {
      setUserNotification(undefined);
    }
  }, [
    notification.userNotification,
    userFilteredNotifications,
    userNotificationList.isFetching,
    userNotificationMarkRead.isLoading,
  ]);

  useEffect(() => {
    if (!!userNotification) {
      action.open();
    } else {
      action.close();
    }
  }, [userNotification]);

  useEffect(() => {
    if (!containerRef.current || !contentRef.current) {
      return;
    }

    const containerWidth = containerRef.current.clientWidth;
    const contentWidth = contentRef.current.clientWidth;

    if (containerWidth < contentWidth) {
      contentRef.current.style.paddingLeft = '100%';
      contentRef.current.style.animationPlayState = 'running';
      contentRef.current.style.transform = '';
      containerRef.current.style.paddingInline = '0px';
    } else {
      contentRef.current.style.paddingLeft = '';
      contentRef.current.style.animationPlayState = 'paused';
      contentRef.current.style.transform = 'translateX(0)';
      containerRef.current.style.paddingInline = '8px';
    }
  }, [width, visible]);

  const markRead = useCallback(async () => {
    try {
      if (!userNotification) return;

      await userNotificationMarkRead.mutateAsync({
        userNotificationIds: [userNotification.userNotificationId],
      });

      setUserNotification(undefined);
    } catch {}
  }, [userNotification]);

  const handleClose = async () => {
    try {
      if (dontShowMarqueeAgain && !!userNotification) {
        await userNotificationMarkAsDismissed.mutateAsync(
          userNotification.notificationId,
        );
      }
    } finally {
      action.close();
    }
  };

  useEffect(() => {
    if (isMobile) {
      message.open({
        content: intl.formatMessage({ id: 'commons.message.info.mobileTable' }),
      });
    }
  }, [isMobile]);

  if (
    !visible ||
    userNotificationList.isFetching ||
    userNotificationMarkRead.isLoading
  )
    return null;

  return (
    <div className="marquee-container" ref={containerRef}>
      <div className="marquee-content" ref={contentRef}>
        <strong>Notificação:</strong>{' '}
        {userNotification?.redirectUrl ? (
          <a
            href={userNotification.redirectUrl}
            target="_blank"
            onClick={markRead}
          >
            {userNotification.message}
          </a>
        ) : (
          userNotification?.message
        )}
      </div>

      {userNotification?.origin === UserNotificationOriginEnum.BANK_SLIP && (
        <Checkbox
          className="marquee-action"
          onChange={({ target }) => setDontShowMarqueeAgain(target.checked)}
        >
          Não mostrar novamente
        </Checkbox>
      )}

      <ActionIcon
        className="marquee-close"
        onClick={handleClose}
        disabled={userNotificationMarkAsDismissed.isLoading}
      >
        {userNotificationMarkAsDismissed.isLoading ? (
          <Spin
            size="small"
            style={{ color: 'white' }}
            indicator={<LoadingOutlined style={{ fontSize: 20 }} spin />}
          />
        ) : (
          <X size={20} />
        )}
      </ActionIcon>
    </div>
  );
}
